import 'animate.css';
import { useEffect } from 'react';
import { useCallback, useState } from 'react';
import { digitsFaToEn } from 'persian-tools2';
import { Navigate, useNavigate } from 'react-router-dom';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import styled from 'styled-components';
import smartViewLogo from 'assets/img/smart-view.svg';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import useTimer from 'hooks/useTimer';
import { useAuthContext } from 'store/context/auth/auth-context';
import {
  useLoginDoctor,
  useSendLoginCode,
  useSignupDoctor
} from 'modules/user/hooks/useRegister';
import {
  LoginBanner,
  LoginForm,
  Header,
  Support,
  CallSupport
} from 'modules/user/components/register';
import { registerSteps } from 'modules/user/constants/register';
import { useMediaQuery } from 'hooks/useMediaQuery';
import { BackButton, Button } from 'modules/components/common';

const Login = () => {
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const navigate = useNavigate();
  const { isLoggedIn } = useAuthContext();

  const { time, startTimer, isRunning, resetTimer } = useTimer();
  const navigateToDashboard = () => navigate('/app/home', { replace: true });

  const sendloginCode = useSendLoginCode();
  const login = useLoginDoctor();
  const signup = useSignupDoctor();

  const [userPhone, setUserPhone] = useState([]);
  const [showCrm, setShowCrm] = useState(false);
  const [backButtonClickCount, setBackButtonClickCount] = useState(0);
  const [showTransition, setShowTransition] = useState(false);
  const [steps, setSteps] = useState(registerSteps.sendloginCode);

  const [initialValues, setInitialValues] = useState({
    phone: '',
    code: '',
    medicalNumber: '',
    nationalCode: ''
  });
  const resetCode = () => setInitialValues((prev) => ({ ...prev, code: '' }));

  const userActivationCodeId = useCallback(
    (phone) => {
      const item =
        userPhone?.length > 0
          ? userPhone.find((item) => item?.phone === phone)
          : null;

      const timestamp = item
        ? ((item?.futurTimeExp - Date.now()) / 1000).toFixed(0)
        : 120;

      const activationCodeIsActive =
        item && timestamp < 120 && timestamp > 0 ? true : false;

      if (item && activationCodeIsActive) {
        resetTimer(timestamp);
        startTimer();
        return {
          activationCodeId: item?.activationCodeId,
          remainingSeconds: timestamp
        };
      }
      return false;
    },
    [userPhone]
  );

  const sendLoginCodeHandler = useCallback(
    (phone, activationCodeId, remainingSeconds) => {
      const condition = {
        signup:
          login?.data?.data?.user.phoneNumber === phone && remainingSeconds,
        login: activationCodeId && remainingSeconds,
        sendRequest: !activationCodeId && !remainingSeconds
      };

      if (condition.signup) {
        setShowTransition(!showTransition);
        setSteps(registerSteps.signup);
      } else if (condition.sendRequest) {
        resetCode();
        sendloginCode.mutate({
          phone,
          setUserPhone,
          userPhone,
          setSteps,
          setShowTransition,
          startTimer,
          resetTimer,
          transition: steps === registerSteps.sendloginCode ? true : false
        });
      } else if (condition.login) {
        setShowTransition(!showTransition);
        setSteps(registerSteps.login);
        resetCode();
      }
    },
    [steps, initialValues]
  );

  const loginHandler = useCallback((code, activationCodeId) => {
    if (code)
      login.mutate({
        activationCodeId,
        code,
        setSteps,
        setShowTransition,
        navigateToDashboard,
        resetTimer
      });
  }, []);

  const signupHandler = useCallback((medicalNumber, nationalCode) => {
    signup.mutate({
      medicalNumber: medicalNumber && digitsFaToEn(medicalNumber),
      nationalCode: nationalCode && digitsFaToEn(nationalCode),
      setSteps,
      navigateToDashboard,
      resetTimer
    });
  }, []);

  const timerHandler = () => {
    if (isRunning) return;
    sendLoginCodeHandler(initialValues.phone);
  };

  const timerProps = {
    timerHandler,
    time,
    resetTimer,
    isRunning,
    startTimer
  };

  const backHandler = useCallback(() => {
    if (steps === registerSteps.sendloginCode) {
      setInitialValues({
        phone: '',
        code: '',
        medicalNumber: '',
        nationalCode: ''
      });
      setShowCrm(false);
      setBackButtonClickCount(0);
      window?.ReactNativeWebView?.postMessage('logout');
    } else if (
      steps === registerSteps.login ||
      steps === registerSteps.signup
    ) {
      setSteps(registerSteps.sendloginCode);
    }
  }, [steps]);

  const onActionHandler = useCallback(
    ({ code, phone, medicalNumber, nationalCode }) => {
      const { activationCodeId, remainingSeconds } =
        userActivationCodeId(phone);

      if (steps === registerSteps.sendloginCode) {
        sendLoginCodeHandler(phone, activationCodeId, remainingSeconds);
      } else if (steps === registerSteps.login) {
        loginHandler(code, activationCodeId);
      } else {
        if (steps === registerSteps.signup) {
          signupHandler(medicalNumber, nationalCode);
        }
      }
    },
    [steps, userPhone]
  );

  const isLoading =
    signup.isLoading || login.isLoading || sendloginCode.isLoading;

  const handlePopstate = (event) => {
    event.preventDefault();
    setBackButtonClickCount(backButtonClickCount + 1);
  };

  useEffect(() => {
    if (backButtonClickCount > 0 && !showCrm) {
      if (steps === registerSteps.login || steps === registerSteps.signup) {
        setSteps(registerSteps.sendloginCode);
      }
    }

    window.history.pushState(null, null, window.location.href);
    window.addEventListener('popstate', handlePopstate);
    return () => {
      window.removeEventListener('popstate', handlePopstate);
    };
  }, [backButtonClickCount]);

  if (isLoggedIn) {
    return <Navigate to="/app/home" replace />;
  }

  return (
    <StyledLogin
      showForm={!showCrm}
      showBackBtn={isDesktop ? steps > registerSteps.sendloginCode : true}
    >
      <div>
        {!isDesktop && <LoginBanner crm={showCrm} />}
        {showCrm && <Support showCrm={showCrm} setShowCrm={setShowCrm} />}
        {!showCrm && <CallSupport setShowCrm={setShowCrm} />}
        <div className="login-form_container">
          {isDesktop && <LoginBanner crm={showCrm} />}
          <Button
            actionHandler={backHandler}
            muiIcon={
              <ArrowBackIcon sx={{ color: isDesktop ? 'gray' : 'white' }} />
            }
            className={'back-btn'}
          />
          <SwitchTransition mode="out-in">
            <CSSTransition
              timeout={250}
              key={showTransition}
              classNames={{
                enterDone: 'animate__animated animate__fadeInRight',
                enterActive: 'animate__animated animate__fadeInRight'
              }}
            >
              <div>
                <div className="header-text">
                  <Header currentStep={steps} phone={initialValues?.phone} />
                </div>
                <LoginForm
                  steps={steps}
                  isLoading={isLoading}
                  timerProps={timerProps}
                  initialValues={initialValues}
                  setInitialValues={setInitialValues}
                  actionHandler={onActionHandler}
                />
              </div>
            </CSSTransition>
          </SwitchTransition>
        </div>
        <div className="smart-view">
          <img src={smartViewLogo} alt="" />
        </div>
      </div>
    </StyledLogin>
  );
};

export default Login;

const StyledLogin = styled.div`
  padding-bottom: 3rem;
  min-height: 100vh;
  .login-form_container {
    padding: 0 17px;
    display: ${({ showForm }) => (showForm ? 'flex' : 'none')};
    flex-direction: column;
  }
  .smart-view {
    display: none;
  }
  .header-text {
    margin: 40px auto 32px;
  }
  .back-btn {
    position: absolute;
    width: 32px;
    min-width: 32px;
    height: 32px;
    min-height: 32px;
    display: ${({ showBackBtn }) => (showBackBtn ? 'block' : 'none')};
    left: 20px;
    top: 20px;
  }
  .back-btn:hover {
    background: transparent;
  }
  @media screen and (min-width: 1024px) {
    padding-bottom: 0;
    display: grid;
    place-items: center;
    height: 100vh;
    width: 100%;
    background-image: radial-gradient(
        85% 85% at top left,
        rgba(60, 192, 195, 0.4) 0%,
        rgba(245, 245, 245, 4e-5) 100%
      ),
      radial-gradient(
        85% 85% at bottom right,
        rgba(117, 60, 190, 0.4) 0%,
        rgba(245, 245, 245, 4e-5) 100%
      );
    background-repeat: no-repeat;
    .smart-view {
      display: ${({ showForm }) => (showForm ? 'flex' : 'none')};
      justify-content: center;
      margin: 39px auto 0;
    }
    .freesens-logo {
      width: 203px;
      height: 50px;
      margin: auto;
    }
    .login-form_container {
      position: relative;
      padding: 50px 34px 40px;
      width: 420px;
      border-radius: 16px;
      background: #f5f5f5;
    }
    .back-btn {
      box-shadow: -2px -2px 7px 0px #ffffff, 1px 1px 10px 0px #0000001a;
      background: #f5f5f5 !important;
      border: 1px solid #ffffff;
      display: ${({ showBackBtn }) => (showBackBtn ? 'flex' : 'none')};
    }
  }
`;
